/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import {
    array, bool, func, object,
} from 'prop-types';

import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useGetAccessTokenSafely from '../../../../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../../../../gql';

import findRecentlyViewedProducts from '../../../../../gql/queries/findRecentlyViewedProducts';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useSalesforceProductRecommendationQuery from '../../../../../helpers/Personalization/useSalesforceProductRecommendationQuery';
import PLARecsBuilder from './PLARecsBuilder';
import useBrowserUUID from '../../../../../helpers/useBrowserUUID';
import PLARecsContainer from './PLARecsContainer';
import noop from '../../../../../helpers/noop';

export default function PLARecentlyViewedLayerContainer({
    product, skuData, isPlaRedesignEnabled, cmsRecBlockData, campaignTrackClickThrough,
}) {
    // 6886; need this query layer as findRecentlyViewedProducts does not retrieve CMS data
    // in refactor change query to: recentlyViewedRecs?
    const browserUUID = useBrowserUUID();
    const brand = useSelector(getBrandName);
    const jwt = useGetAccessTokenSafely();
    const isPersonalizationHoldoutEnabled = useSelector(getFeatureFlag('is-personalization-holdout-enabled'));

    const {
        loading, error, data,
    } = useQuery(findRecentlyViewedProducts(), {
        variables: {
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            user: {
                anonymousId: browserUUID,
            },
        },
        context: {
            api: 'personalizedContent',
            jwt,
        },
        skip: !jwt || !browserUUID,
        fetchPolicy: 'network-only',
    });

    if (!jwt || !browserUUID) {
        return null;
    }

    if (loading) {
        return null;
    }

    if (error) {
        mbpLogger.logError({
            function: 'PLARecentlyViewedLayerContainer.js',
            message: 'Error loading data for recently viewed products',
            appName: process.env.npm_package_name,
            module: 'PLARecentlyViewedLayerContainer',
            error,
        });
        return null;
    }

    const removeCurrentProduct = ({ currentPartNumber, products }) => products.filter((elem) => elem.partNumber !== currentPartNumber);

    const rvProducts = removeCurrentProduct({ currentPartNumber: product.partNumber, products: data?.recentlyViewed?.products?.length > 0 ? data.recentlyViewed.products : [] });

    if (rvProducts?.length > 0) {
        return (
            <PLARecsContainer
                product={product}
                skuData={skuData}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                cmsRecBlockData={cmsRecBlockData}
                rvProducts={rvProducts}
                isFromRVOnPLA
                campaignTrackClickThrough={campaignTrackClickThrough}
            />
        );
    }
    return null;
}

PLARecentlyViewedLayerContainer.propTypes = {
    product: object.isRequired,
    skuData: array.isRequired,
    isPlaRedesignEnabled: bool.isRequired,
    cmsRecBlockData: object,
    campaignTrackClickThrough: func,
};

PLARecentlyViewedLayerContainer.defaultProps = {
    cmsRecBlockData: {},
    campaignTrackClickThrough: noop,
};
